import React from "react";
import { Line } from "react-chartjs-2";
import { Bar } from "react-chartjs-2";
import {
    Chart as ChartJS,
    LineElement,
    CategoryScale,
    LinearScale,
    PointElement,
    Tooltip,
    Legend,
    BarElement,
} from "chart.js";


ChartJS.register(LineElement, CategoryScale, LinearScale, PointElement, BarElement, Tooltip, Legend);

const CHART_STYLES = `
  @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,opsz,wght@0,9..144,500;0,9..144,600&family=Manrope:wght@400;500;600;700;800&display=swap');
  .rc-display { font-family: 'Fraunces', ui-serif, Georgia, serif; }
  .rc-body { font-family: 'Manrope', ui-sans-serif, system-ui, sans-serif; }
`;

const RevenueCharts = ({ revenueData, dateRange }) => {
    const revenue = revenueData.revenue.map((n) => +n);
    const impressions = revenueData.impressions.map((n) => +n);

    const datasets = [];
    if (revenue) {
        datasets.push({
            label: "Revenue ($)",
            data: revenue,
            backgroundColor: "rgba(217, 119, 6, 0.75)", // amber-600
            hoverBackgroundColor: "rgba(180, 83, 9, 0.9)", // amber-700
            borderColor: "rgba(180, 83, 9, 1)", // amber-700
            yAxisID: "yRevenue",
            borderRadius: 6,
        });
    }
    if (impressions) {
        datasets.push({
            label: "Impressions",
            data: impressions,
            borderColor: "#92400E", // amber-800
            backgroundColor: "rgba(146, 64, 14, 0.15)",
            pointBackgroundColor: "#92400E",
            pointBorderColor: "#ffffff",
            pointHoverRadius: 6,
            fill: true,
            tension: 0.3,
            yAxisID: "yImps",
            borderRadius: 4,
        });
    }

    const dataBar = {
        labels: revenueData.label,
        datasets
    };

    const optionsBar = {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
            legend: {
                position: 'top',
                labels: {
                    color: "#4D3E30",
                    font: { size: 12, weight: "bold" },
                    boxWidth: 14,
                    padding: 16,
                },
            },
            title: {
                display: true,
                text: dateRange === "Custom Date"
                    ? "Custom Date Metrics"
                    : `${dateRange} Metrics`,
                color: "#1C1410",
                font: { size: 16, weight: "bold" },
                padding: { bottom: 16 },
            },
        },
        scales: {
            yRevenue: {
                type: "linear",
                position: "left",
                beginAtZero: true,
                title: { display: true, text: "Revenue ($)", color: "#8A7560" },
                ticks: { color: "#D97706" },
                grid: { color: "#F5DFC5" },
            },
            yImps: {
                type: "linear",
                position: "right",
                beginAtZero: true,
                title: { display: true, text: "Impressions", color: "#8A7560" },
                grid: { drawOnChartArea: false },
                ticks: { color: "#92400E" },
            },
            x: {
                ticks: { color: "#8A7560", maxTicksLimit: 20 },
                grid: { color: "#F9EFE0" },
            },
        },
    };

    const heading =
        dateRange === "Today"
            ? "Today's Revenue"
            : dateRange === "Yesterday"
                ? "Yesterday's Revenue"
                : dateRange === "Last 7 Days"
                    ? "Last 7 Days Revenue"
                    : dateRange === "Last 30 Days"
                        ? "One Month Revenue"
                        : dateRange === "Custom Date"
                            ? "Custom Date Revenue"
                            : null;

    const useLineChart =
        dateRange === "Last 7 Days" ||
        dateRange === "Last 30 Days" ||
        dateRange === "Custom Date";

    if (!heading) return <></>;

    return (
        <div className="rc-body w-full p-4 grid grid-cols-1 gap-4">
            <style>{CHART_STYLES}</style>
            <div
                className="bg-white border border-[#F0DCC8] shadow-[0_10px_30px_-18px_rgba(22,34,27,0.25)] w-full p-4 sm:p-6"
                style={{ borderRadius: "1.5rem" }}
            >
                <div className="flex items-center gap-3 mb-3">
                    <span
                        aria-hidden="true"
                        className="h-6 w-1 rounded-full"
                        style={{ background: "linear-gradient(180deg, #D97706, #EA580C)" }}
                    />
                    <h2 className="rc-display text-base sm:text-lg font-semibold text-[#1C1410]">
                        {heading}
                    </h2>
                </div>
                <div className="relative w-full h-[280px] sm:h-[360px] lg:h-[420px]">
                    {useLineChart ? (
                        <Line data={dataBar} options={optionsBar} />
                    ) : (
                        <Bar data={dataBar} options={optionsBar} />
                    )}
                </div>
            </div>
        </div>
    );
};

export default RevenueCharts;